import React, { createContext, useContext, useState, ReactNode } from 'react';
import {
  Backdrop,
  Box,
  CircularProgress,
  IconButton,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

interface LoaderContextType {
  showLoader: (options: LoaderOptions) => Promise<void>;
}

interface LoaderOptions {
  task: () => Promise<any>;
  loadingText?: string;
  errorHandler?: () => Promise<void>;
  errorHandlerText?: string;
}

interface LoaderState {
  isLoading: boolean;
  loadingText: string;
  error: string | null;
  errorHandler?: () => Promise<void>;
  errorHandlerText?: string;
}

export const LoaderContext = createContext<LoaderContextType | undefined>(
  undefined,
);

export const LoaderProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [state, setState] = useState<LoaderState>({
    isLoading: false,
    loadingText: '',
    error: null,
  });

  const showLoader = async ({
    task,
    loadingText = 'Loading...',
    errorHandler,
    errorHandlerText,
  }: LoaderOptions) => {
    setState({
      isLoading: true,
      loadingText,
      error: null,
      errorHandler,
      errorHandlerText,
    });

    try {
      await task();
    } catch (error) {
      setState((prev) => ({
        ...prev,
        error: error instanceof Error ? error.message : 'An error occurred',
      }));
      return;
    }

    setState({
      isLoading: false,
      loadingText: '',
      error: null,
    });
  };

  const handleErrorClose = async () => {
    if (state.errorHandler) {
      setState((prev) => ({
        ...prev,
        loadingText: prev.errorHandlerText || 'Processing...',
        error: null,
      }));

      try {
        await state.errorHandler();
      } catch (error) {
        setState((prev) => ({
          ...prev,
          error: error instanceof Error ? error.message : 'An error occurred',
        }));
        return;
      }
    }

    setState({
      isLoading: false,
      loadingText: '',
      error: null,
    });
  };

  return (
    <LoaderContext.Provider value={{ showLoader }}>
      {children}
      <Backdrop
        sx={{
          color: '#fff',
          zIndex: (theme) => theme.zIndex.drawer + 1,
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
        }}
        open={state.isLoading}
      >
        {state.error ? (
          <Box
            sx={{
              position: 'relative',
              bgcolor: 'rgba(0, 0, 0, 0.8)',
              p: 4,
              borderRadius: 1,
              maxWidth: '80%',
            }}
          >
            {state.errorHandler && (
              <IconButton
                sx={{
                  position: 'absolute',
                  right: 8,
                  top: 8,
                  color: 'white',
                }}
                onClick={handleErrorClose}
              >
                <CloseIcon />
              </IconButton>
            )}
            <Typography color="error" align="center">
              {state.error}
            </Typography>
          </Box>
        ) : (
          <>
            <CircularProgress color="inherit" />
            {state.loadingText && (
              <Typography variant="h6" sx={{ mt: 2 }}>
                {state.loadingText}
              </Typography>
            )}
          </>
        )}
      </Backdrop>
    </LoaderContext.Provider>
  );
};

export const useLoader = () => {
  const context = useContext(LoaderContext);
  if (context === undefined) {
    throw new Error('useLoader must be used within a LoaderProvider');
  }
  return context;
};
